import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../hooks/useTranslation';
import BuySugarModal from './BuySugarModal';
import SugarTruckConfirmModal from './SugarTruckConfirmModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const MoreDetailsModal = ({ load, onClose, onBookSugarOnly, onBookBundle }) => {
  const { t } = useTranslation();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const [isBuySugarModalOpen, setIsBuySugarModalOpen] = useState(false);
  const [isSugarTruckModalOpen, setIsSugarTruckModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const handleBuySugar = () => {
    if (!user || !user?.id) { setShowLoginModal(true); return; }
    setIsBuySugarModalOpen(true);
  };

  const handleSugarTruck = () => {
    if (!user || !user?.id) { setShowLoginModal(true); return; }
    setIsSugarTruckModalOpen(true);
  };

  const handleBuySugarConfirm = (purchaseData) => {
    onBookSugarOnly(purchaseData);
    setIsBuySugarModalOpen(false);
    onClose();
  };

  const handleSugarTruckConfirm = (bundleData) => {
    onBookBundle(bundleData);
    setIsSugarTruckModalOpen(false);
    onClose();
  };

  // Real factory photos from load prop
  const realPhotos = [
    load?.factoryPhotoUrl && { id: 1, url: load.factoryPhotoUrl, alt: 'Factory' },
    load?.productPhotoUrl && { id: 2, url: load.productPhotoUrl, alt: 'Product' },
    load?.packingPhotoUrl && { id: 3, url: load.packingPhotoUrl, alt: 'Packing' },
  ].filter(Boolean);


  const mockReviews = [
  { id: 1, rating: 4.5, buyer: 'Rajesh Traders', comment: 'Excellent quality sugar. Timely delivery and professional service.', date: '2025-11-15' },
  { id: 2, rating: 5, buyer: 'Krishna Sugar Corp', comment: 'Best quality S30 grade. Highly recommended mill.', date: '2025-11-10' },
  { id: 3, rating: 4, buyer: 'Mumbai Sweets Ltd', comment: 'Good packaging and fair pricing. Will order again.', date: '2025-11-05' }];


  const millDistance = '127 km'; // Mock distance
  const estimatedDeliveryTime = '3-4 hours';
  const certifications = ['FSSAI', 'ISO 9001:2015', 'BIS Certified', 'Quality Mark'];

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1300] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-white">Complete Details - {load?.product}</h2>
              <p className="text-sm text-blue-100 mt-1">{load?.offerId} • {load?.millName}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors text-white text-xl font-bold"
              aria-label="Close">

              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="sticky top-[72px] bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-10">
            <div className="flex gap-1 px-6">
              <button
                onClick={() => setActiveTab('details')}
                className={`px-4 py-3 text-sm font-semibold transition-all ${
                activeTab === 'details' ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`
                }>

                <Icon name="FileText" size={16} className="inline mr-2" />
                Details
              </button>
              <button
                onClick={() => setActiveTab('photos')}
                className={`px-4 py-3 text-sm font-semibold transition-all ${
                activeTab === 'photos' ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`
                }>

                <Icon name="Image" size={16} className="inline mr-2" />
                Photos
              </button>
              <button
                onClick={() => setActiveTab('location')}
                className={`px-4 py-3 text-sm font-semibold transition-all ${
                activeTab === 'location' ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`
                }>

                <Icon name="MapPin" size={16} className="inline mr-2" />
                Location
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`px-4 py-3 text-sm font-semibold transition-all ${
                activeTab === 'reviews' ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`
                }>

                <Icon name="Star" size={16} className="inline mr-2" />
                Reviews
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
            {/* Details Tab */}
            {activeTab === 'details' &&
            <div className="space-y-6">
                {/* Product Specifications */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 border-2 border-blue-200 dark:border-blue-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Icon name="Package" size={20} className="text-blue-600" />
                    Product Specifications
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Product Type</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{load?.product}</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Grade</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{load?.grade}</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Available Quantity</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{load?.quantity} MT</p>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Rate per Ton</p>
                      <p className="font-semibold text-green-600 dark:text-green-400">{formatCurrency(load?.rate)}</p>
                    </div>
                  </div>
                </div>

                {/* Mill Details */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-5 border-2 border-purple-200 dark:border-purple-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Icon name="Building2" size={20} className="text-purple-600" />
                    Mill Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Mill Name</p>
                      <p className="font-semibold text-gray-900 dark:text-white text-lg">{load?.millName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{load?.millCode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Location</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{load?.deliveryFrom}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Zone: {load?.zone}</p>
                    </div>
                  </div>
                </div>
              </div>
            }

            {/* Photos Tab */}
            {activeTab === 'photos' &&
            <div className="space-y-4">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-5 border-2 border-indigo-200 dark:border-indigo-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Icon name="Camera" size={20} className="text-indigo-600" />
                    Mill & Product Photos
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {realPhotos.length > 0 ? realPhotos.map((photo) =>
                  <div key={photo?.id} className="relative group overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300">
                        <img
                      src={photo?.url}
                      alt={photo?.alt}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300" />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-white text-sm font-semibold">{photo?.alt}</p>
                          </div>
                        </div>
                      </div>
                  ) : (
                    <div className="col-span-3 text-center py-10 text-gray-400">
                      <Icon name="ImageOff" size={32} className="mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No photos uploaded yet</p>
                    </div>
                  )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
                    Click on any photo to view in full size
                  </p>
                </div>
              </div>
            }

            {/* Location Tab */}
            {activeTab === 'location' &&
            <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-5 border-2 border-blue-200 dark:border-blue-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Icon name="MapPin" size={20} className="text-blue-600" />
                    Location & Distance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <Icon name="Building2" size={20} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Mill Location</p>
                            <p className="font-semibold text-gray-900 dark:text-white">{load?.deliveryFrom}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{load?.millName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Zone: {load?.zone}</p>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                            <Icon name="Navigation" size={20} className="text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Distance from Your Location</p>
                            <p className="font-bold text-gray-900 dark:text-white text-lg">{millDistance}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Estimated Travel Time: {estimatedDeliveryTime}</p>
                      </div>

                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <Icon name="Route" size={20} className="text-purple-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Best Route</p>
                            <p className="font-semibold text-gray-900 dark:text-white">Via National Highway</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Primary route with good road conditions</p>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden h-[400px]">
                      <div className="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Map" size={64} className="mx-auto mb-4 text-gray-400" />
                          <p className="text-gray-600 dark:text-gray-400 font-semibold">Interactive Map</p>
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Map integration coming soon</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Transportation Note</p>
                      <p className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                        Distance and travel time are estimates. Actual values may vary based on traffic conditions and route selection.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            }

            {/* Reviews Tab */}
            {activeTab === 'reviews' &&
            <div className="space-y-4">
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl p-5 border-2 border-yellow-200 dark:border-yellow-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Icon name="Star" size={20} className="text-yellow-600" />
                    Buyer Reviews & Ratings
                  </h3>
                  
                  {/* Overall Rating */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 mb-6">
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="text-5xl font-bold text-yellow-600 dark:text-yellow-400">4.5</div>
                        <div className="flex items-center justify-center gap-1 mt-2">
                          {[1, 2, 3, 4, 5]?.map((star) =>
                        <Icon
                          key={star}
                          name="Star"
                          size={16}
                          className={star <= 4.5 ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} />

                        )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Based on {mockReviews?.length} reviews</p>
                      </div>
                      <div className="flex-1 space-y-2">
                        {[5, 4, 3, 2, 1]?.map((rating) =>
                      <div key={rating} className="flex items-center gap-3">
                            <div className="flex items-center gap-1 w-16">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{rating}</span>
                              <Icon name="Star" size={14} className="text-yellow-500" />
                            </div>
                            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                            className="h-full bg-yellow-500"
                            style={{ width: rating === 5 ? '66%' : rating === 4 ? '33%' : '0%' }} />

                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400 w-8 text-right">
                              {rating === 5 ? '2' : rating === 4 ? '1' : '0'}
                            </span>
                          </div>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* Individual Reviews */}
                  <div className="space-y-4">
                    {mockReviews?.map((review) =>
                  <div key={review?.id} className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">{review?.buyer}</p>
                            <div className="flex items-center gap-1 mt-1">
                              {[1, 2, 3, 4, 5]?.map((star) =>
                          <Icon
                            key={star}
                            name="Star"
                            size={14}
                            className={star <= review?.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} />

                          )}
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-2">
                                {review?.rating}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(review?.date)}</p>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{review?.comment}</p>
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          <button className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center gap-1">
                            <Icon name="ThumbsUp" size={14} />
                            Helpful (5)
                          </button>
                          <button className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center gap-1">
                            <Icon name="MessageCircle" size={14} />
                            Reply
                          </button>
                        </div>
                      </div>
                  )}
                  </div>
                </div>
              </div>
            }
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex justify-end gap-3">
              <Button
                variant="default"
                size="lg"
                onClick={handleBuySugar}
                className="min-w-[160px] bg-blue-600 hover:bg-blue-700 text-white font-bold">

                Buy Sugar Only
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={handleSugarTruck}
                disabled={load?.trucksAvailable === 0}
                className="min-w-[180px] bg-green-600 hover:bg-green-700 text-white font-bold disabled:bg-gray-400">

                Sugar+Truck Bundle
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Buy Sugar Modal */}
      {isBuySugarModalOpen &&
      <BuySugarModal
        load={load}
        onClose={() => setIsBuySugarModalOpen(false)}
        onConfirm={handleBuySugarConfirm} />

      }
      {/* Sugar+Truck Confirmation Modal */}
      {isSugarTruckModalOpen &&
      <SugarTruckConfirmModal
        load={load}
        onClose={() => setIsSugarTruckModalOpen(false)}
        onConfirm={handleSugarTruckConfirm} />

      }
      {showLoginModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center" style={{ backgroundColor: '#ffffff' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="LogIn" size={26} color="#d97706" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ color: '#1f2937' }}>Login Required</h3>
            <p className="text-sm mb-5" style={{ color: '#6b7280' }}>Please login or register to place your order.</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowLoginModal(false); navigate('/login-authentication'); }}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#d97706', color: '#ffffff' }}
              >
                Login
              </button>
              <button
                onClick={() => { setShowLoginModal(false); navigate('/user-registration'); }}
                className="flex-1 py-2.5 rounded-xl border text-sm font-bold"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                Register
              </button>
            </div>
            <button onClick={() => setShowLoginModal(false)} className="mt-4 text-xs underline" style={{ color: '#9ca3af' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>);

};

export default MoreDetailsModal;

